import { Alert, Button, Card, Col, Form, Input, message, Modal, Row, Select, Spin } from 'antd'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { PHONE_PATTERN } from '../../constants/general'
import InstitutionUserService from '../../services/InstitutionUserService'
import { parseMoneyAmount } from '../../utils/parseMoneyAmount'
import { RefundStatsRangeAlert } from '../refund/RefundStatsRangeAlert'
import TextArea from 'antd/es/input/TextArea'
import { v4 as uuidv4 } from 'uuid'
import styles from '../../asserts/css/UpdateForPublicTreasury.module.css'

interface RefundStats {
  bailiffAmountRequestedToBeRefunded: number
  biddersAmountRequestedToBeRefund: number
  newBiddersAmountRequestedToBeRefunded: number
  newBailiffAmountRequestedToBeRefunded: number
  totalPayments: number
}

// Define the component
const UpdateForPublicTreasury: React.FC = () => {
  const userInfo = JSON.parse(localStorage.getItem('userInfo') ?? '{}')
  const [form] = Form.useForm()
  const { Option } = Select
  const [loadingRefund, setLoadingRefund] = useState(false)
  const [declarationChecked, setDeclarationChecked] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [biddersStatus, setBiddersStatus] = useState(false)
  const [showOTPForm, setShowOTPForm] = useState(false)
  const [phone_number, setPhoneNumber] = useState('')
  const [showWarningAlert, setShowWarningAlert] = useState(false)
  const [showSuccessAlert, setShowSuccessAlert] = useState(false)
  const [showCancelMessage, setShowCancelMessage] = useState(false)
  const [showRequestExistsModal, setShowRequestExistsModal] = useState(false)
  const [requestExistsErrorMessage, setRequestExistsErrorMessage] = useState('')
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [paymentDate, setPaymentDate] = useState('')
  const [token, setToken] = useState('')
  const [bidderReferenceNumber, setBidderReferenceNumber] = useState('')
  const [BiddersAmountRequestedToBeRefund, setBiddersAmountRequestedToBeRefund] = useState('')
  const [BailiffAmountRequestedToBeRefunded, setBailiffAmountRequestedToBeRefunded] = useState('')
  const [NewBiddersAmountRequestedToBeRefunded, setNewBiddersAmountRequestedToBeRefunded] = useState('')
  const [NewBailiffAmountRequestedToBeRefunded, setNewBailiffAmountRequestedToBeRefunded] = useState('')
  const [generatedLink, setGeneratedLink] = useState<string>('')
  const [refundStats, setRefundStats] = useState<RefundStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [totalPaidForRange, setTotalPaidForRange] = useState('')

  const [ipAddress, setIpAddress] = useState<string>('')
  const [deviceName, setDeviceName] = useState<string>('')
  const fromDate = '2024-07-01' // Example fromDate (can be dynamic)
  const toDate = '2024-07-31'
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleContinue = () => {
    if (declarationChecked) {
      setShowOTPForm(true)
    }
  }

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const fetchIpAddress = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json')
        const data = await response.json()
        setIpAddress(data.ip)
        // eslint-disable-next-line @typescript-eslint/no-shadow
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch IP address:', error)
      }
    }
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const fetchDeviceName = () => {
      const platform = navigator.platform
      const userAgent = navigator.userAgent
      setDeviceName(`${platform} - ${userAgent}`)
    }

    void fetchIpAddress()
    fetchDeviceName()
  }, [])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    async function fetchRefundStats () {
      setLoading(true)
      try {
        const response = await InstitutionUserService.viewRefundStatsByRefundStartDate(fromDate, toDate, token)
        setRefundStats(response.data)
        const biddersAmountRequestedToBeRefund = response.data?.biddersAmountRequestedToBeRefund
        setBiddersAmountRequestedToBeRefund(response.data?.biddersAmountRequestedToBeRefund)
        const bailiffAmountRequestedToBeRefunded = response.data?.bailiffAmountRequestedToBeRefunded
        setBailiffAmountRequestedToBeRefunded(response.data?.bailiffAmountRequestedToBeRefunded)
        const newBiddersAmountRequestedToBeRefunded = response.data?.newBiddersAmountRequestedToBeRefunded
        setNewBiddersAmountRequestedToBeRefunded(response.data?.newBiddersAmountRequestedToBeRefunded)
        const newBailiffAmountRequestedToBeRefunded = response.data?.newBailiffAmountRequestedToBeRefunded
        setNewBailiffAmountRequestedToBeRefunded(response.data?.newBailiffAmountRequestedToBeRefunded)
        const tp = response.data?.totalPayments
        setTotalPaidForRange(tp != null && tp !== '' ? String(tp) : '')
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        setError('Failed to fetch refund stats')
        // eslint-disable-next-line no-console
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    void fetchRefundStats()
  },
  [fromDate, toDate, token])

  // If loading, show a loading message
  if (loading) {
    return <div>Loading...</div>
  }

  // If error occurs, show an error message
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (error) {
    return <div>{error}</div>
  }

  // If refundStats is null or doesn't have the necessary data, show a fallback UI
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (!refundStats) {
    return <div>No data available</div>
  }

  const handleReferenceChange = async (value: string): Promise<void> => {
    setBidderReferenceNumber(value)
    if (value !== '') {
      setLoadingRefund(true)
      try {
        const paymentData = await InstitutionUserService.loadBidsDetailsForRefund(value)
        if (paymentData.status === 200) {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/no-shadow
          const encryptReferenceNumber = (value: any) => {
            // Generate a random string to add
            const randomString = Math.random().toString(36).substring(2, 8) // Adjust length as needed
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            const combinedValue = `${randomString}-${value}-${randomString}`
            const utf8Value = unescape(encodeURIComponent(combinedValue))
            return btoa(utf8Value)
          }
          const encryptedReference = encryptReferenceNumber(paymentData.data?.ref_number)
          form.setFieldsValue({
            bidder_id: paymentData.data?.bid?.bidder?.bidder_id,
            bid_security_id: paymentData.data?.payment?.bid_security_id,
            amount: paymentData.data?.payment?.amount,
            transaction_currency: paymentData.data?.payment?.currency,
            transaction_reference: paymentData.data?.ref_number,
            phone_number: paymentData.data?.bid?.bidder?.phone_number,
            bidder_national_id_number: paymentData.data?.bid?.bidder?.nid,
            property_id: paymentData.data?.bid?.auction?.property_id,
            execution_case_number: paymentData.data?.bid?.auction?.execution_case_no,
            fullname: paymentData.data?.bid?.bidder?.full_name,
            email: paymentData.data?.bid?.bidder?.email,
            payment_date: paymentData.data?.payment?.payment_date
          })
          setPaymentDate(paymentData.data?.payment?.payment_date)
          setBiddersStatus(paymentData.status)
        } else {
          throw new Error('No data found for the given reference number.')
        }
        // eslint-disable-next-line @typescript-eslint/no-shadow
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'An unexpected error occurred.'
        Modal.error({
          title: 'Error',
          content: errorMessage
        })
      } finally {
        setLoadingRefund(false)
      }
    }
  }
  // Handle search button click
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSearchClick = () => {
    const referenceNumber = form.getFieldValue('bid_reference_number')
    void handleReferenceChange(referenceNumber)
    setShowForm(true)
    setBidderReferenceNumber(form.getFieldValue('bid_reference_number'))
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const onFinish = async (values: any): void => {
    const payload = {
      RefundRequest: {
        bank_code: '00',
        transaction_reference: values.transaction_reference,
        debit_account_number: '000400696575429',
        debit_account_name: 'MINIJUST AUCTION FUNDS MAF',
        transaction_currency: values.transaction_currency,
        beneficiary_account_name: 'OTR ACCOUNT',
        beneficiary_bank_name: 'BANQUE NATIONALE DU RWANDA (BNR)',
        beneficiary_account_number: '1000007559',
        amount: form.getFieldValue('amount'),
        reason: 'Updated to public treasury.',
        status: 'PUBLIC TREASURY',
        bidder_id: values.bidder_id,
        bid_security_id: values.bid_security_id,
        phone_number: values.phone_number,
        email: form.getFieldValue('email'),
        bailiff_phone_number: form.getFieldValue('bailiff_phone_number'),
        bailiff_national_id: form.getFieldValue('bailiff_national_id'),
        bailiff_assignee: form.getFieldValue('assignee-bailiff'),
        description: form.getFieldValue('description'),
        created_by: userInfo.full_name
      }
    }
    try {
      const body = {
        transaction_reference: bidderReferenceNumber
      }
      const validateRequest = await InstitutionUserService.validateRefundRequest(body)
      if (validateRequest === null || validateRequest.status === 200) {
        setShowRequestExistsModal(true)
        return
      }
      // eslint-disable-next-line @typescript-eslint/no-shadow
    } catch (error) {
      // Send the refund request
      try {
        const res = await InstitutionUserService.UpdateToPublicTreasury(payload, token)
        if (res.status === 200 || res.status === 201) {
          const generatedUuid = uuidv4()
          const param = { transaction_reference: values.transaction_reference }
          const getLastRefundReferenceNumber = await InstitutionUserService.getRefundRequestId(param)

          const refundRequestId = getLastRefundReferenceNumber?.data?.refund_request_id

          if (refundRequestId !== null) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions,no-console
            console.log(`Refund Request ID: ${refundRequestId}`)
          } else {
            // eslint-disable-next-line no-console
            console.log('Unable to retrieve Refund Request ID')
          }
          const AuditPayload = {
            AuditLogsData: {
              action: 'INSERT',
              table_name: 'refund_request',
              record_id: generatedUuid,
              old_data: payload,
              new_data: {},
              created_by: userInfo.full_name,
              refund_request_id: refundRequestId
            }
          }
          await InstitutionUserService.TrackRecords(AuditPayload)
          const apiMessage = res.data?.message != null ? String(res.data.message) : (res.message != null ? String(res.message) : '5% of Bid Security has been updated to public treasury')
          setSuccessMessage(apiMessage)
          setShowSuccessModal(true)
        } else if (res.status === 400 || res.status === 404) {
          // Display modal for "Request already exists" error
          const dataMessage = res.data?.message != null ? String(res.data.message) : ''
          const resMessage = res.message != null ? String(res.message) : ''
          const errorMessage = dataMessage !== '' ? dataMessage : (resMessage !== '' ? resMessage : '')
          if (errorMessage !== '') {
            setRequestExistsErrorMessage(errorMessage)
            setShowRequestExistsModal(true)
          } else {
            void message.error('Failed to submit the refund request. Please try again.')
          }
        } else {
          void message.error('Failed to submit the refund request. Please try again.')
        }
      } catch (apiError: any) {
        // Handle API errors (thrown exceptions)
        const errorStatus = apiError?.response?.status
        const responseMessage = apiError?.response?.data?.message != null ? String(apiError.response.data.message) : ''
        const apiErrorMessage = apiError?.message != null ? String(apiError.message) : ''
        const errorMessage = responseMessage !== '' ? responseMessage : (apiErrorMessage !== '' ? apiErrorMessage : '')
        if ((errorStatus === 400 || errorStatus === 404) && errorMessage !== '') {
          // Display modal for status 400 or 404
          setRequestExistsErrorMessage(errorMessage)
          setShowRequestExistsModal(true)
        } else if (errorMessage !== '') {
          void message.error(errorMessage)
        } else {
          void message.error('An error occurred while sending the refund request. Please try again later.')
        }
      }
    } finally {
      setLoadingRefund(false)
    }
  }

  return (
    <div className="w-full">
      <div className="px-5 py-4 border-b-slate-200 border border-solid w-full" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderRadius: '0 0 12px 12px',
        marginBottom: '20px'
      }}>
        <div className="flex justify-between items-center">
          <div className="text-title font-bold flex items-center">
            <div style={{
              background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '16px',
              boxShadow: '0 4px 12px rgba(55, 118, 47, 0.3)'
            }}>
              <span style={{ fontSize: '24px' }}>🏦</span>
            </div>
            <div>
              <h2 className="text-title font-bold" style={{
                margin: 0,
                background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Update for Public Treasury
              </h2>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '14px',
                color: '#6b7280',
                fontWeight: '500'
              }}>
                💳 Update Refund Transfer to Public Treasury • Status Management
              </p>
            </div>
          </div>
          <div className="md:w-1/5"></div>
        </div>
      </div>
      <RefundStatsRangeAlert fromDate={fromDate} toDate={toDate} />
      {/* Enhanced stats section with hover effects - Responsive */}
      <div style={{
        padding: '20px',
        backgroundColor: '#fafafa',
        borderRadius: '8px',
        margin: '0 20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
      className={styles['stats-container']}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={5}>
            <div
              className="stat-card"
              style={{
                width: '100%',
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                textAlign: 'center',
                color: '#111827',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.12)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)'
              }}
            >
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: 'bold' }}>
                Total Amount Requested
              </div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#37762f' }}>
                {((parseMoneyAmount(BailiffAmountRequestedToBeRefunded) ?? 0) + (parseMoneyAmount(BiddersAmountRequestedToBeRefund) ?? 0)).toLocaleString()} Rwf
              </div>
            </div>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={5}>
            <div
              className="stat-card"
              style={{
                width: '100%',
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                textAlign: 'center',
                color: '#111827',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.12)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)'
              }}
            >
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: 'bold' }}>
                New Amount Requested
              </div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#37762f' }}>
                {((parseMoneyAmount(NewBiddersAmountRequestedToBeRefunded) ?? 0) + (parseMoneyAmount(NewBailiffAmountRequestedToBeRefunded) ?? 0)).toLocaleString()} Rwf
              </div>
            </div>
          </Col>

          <Col xs={24} sm={12} md={8} lg={4} xl={5} xxl={5}>
            <div
              className="stat-card"
              style={{
                width: '100%',
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                textAlign: 'center',
                color: '#111827',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.12)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)'
              }}
            >
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: 'bold' }}>
                Remaining to Request
              </div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#37762f' }}>
                {Math.max(
                  0,
                  parseMoneyAmount(totalPaidForRange) - (parseMoneyAmount(BiddersAmountRequestedToBeRefund) + parseMoneyAmount(BailiffAmountRequestedToBeRefunded))
                ).toLocaleString()} Rwf
              </div>
            </div>
          </Col>

          <Col xs={24} sm={12} md={8} lg={4} xl={5} xxl={5}>
            <div
              className="stat-card"
              style={{
                width: '100%',
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                textAlign: 'center',
                color: '#111827',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.12)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)'
              }}
            >
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: 'bold' }}>
                Total Paid Amount
              </div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#37762f' }}>
                {(parseMoneyAmount(totalPaidForRange)).toLocaleString()} Rwf
              </div>
            </div>
          </Col>

          <Col xs={24} sm={12} md={8} lg={4} xl={4} xxl={4}>
            <div
              className="stat-card"
              style={{
                width: '100%',
                background: '#f8fafc',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                textAlign: 'center',
                color: '#111827',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                cursor: 'default'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.12)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)'
              }}
            >
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px', fontWeight: 'bold' }}>
                Total Refunded Amount
              </div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#37762f' }}>
                {(((parseMoneyAmount(BiddersAmountRequestedToBeRefund) + parseMoneyAmount(BailiffAmountRequestedToBeRefunded))) - ((parseMoneyAmount(NewBiddersAmountRequestedToBeRefunded)) + (parseMoneyAmount(NewBailiffAmountRequestedToBeRefunded)))).toLocaleString()} Rwf
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <div className="px-2">
        <div className="py-0 mb-0">
          <Form
            name="normal_login"
            layout="vertical"
            className="login-form p-10"
            autoComplete="off"
            form={form}
            initialValues={{ remember: true }}
            onFinish={onFinish}
          >
            <Card
              className="bg-gradient-to-r from-blue-50 to-indigo-50"
              style={{
                borderRadius: '12px',
                border: '1px solid #e0e7ff',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
              }}
            >
              <div style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
                border: '1px solid #dbeafe',
                borderRadius: '12px',
                padding: '14px',
                marginBottom: '8px'
              }}>
                <Row gutter={[12, 8]}>
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Form.Item
                      label={<span style={{ fontWeight: '700', color: '#374151' }}>* Bid Reference Number</span>}
                      name="bid_reference_number"
                      rules={[
                        {
                          required: true,
                          message: 'Please enter Bid Reference Number'
                        },
                        {
                          pattern: /^[A-Za-z\d!@#$%^&*()_+={}[\]:;"'<>,.?/~`\\|-]{7}$/,
                          message:
                            'Bid Reference Number must be exactly 7 characters long and can include letters, digits, and special characters.'
                        }
                      ]}
                      style={{ marginBottom: '6px' }}
                    >
                      <Input
                        type="tel"
                        placeholder="Enter Bid Reference Number"
                        size="large"
                        allowClear
                        style={{
                          borderRadius: '10px',
                          border: '1px solid #d1d5db',
                          height: '46px'
                        }}
                      />
                    </Form.Item>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                      Enter the exact 7-character bid reference to load details.
                    </p>
                  </Col>
                  <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Form.Item style={{ marginBottom: 0, marginTop: '8px' }}>
                      <Button
                        type="primary"
                        onClick={handleSearchClick}
                        loading={loadingRefund}
                        size="large"
                        className="font-bold"
                        style={{
                          background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
                          border: 'none',
                          borderRadius: '10px',
                          height: '46px',
                          minWidth: '220px',
                          fontSize: '16px',
                          fontWeight: '700',
                          letterSpacing: '0.3px',
                          boxShadow: '0 6px 16px rgba(55, 118, 47, 0.25)',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-1px)'
                          e.currentTarget.style.boxShadow = '0 8px 18px rgba(55, 118, 47, 0.3)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(55, 118, 47, 0.25)'
                        }}
                      >
                        🔍 SEARCH
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {showForm && biddersStatus && (
                <div style={{
                  padding: '12px 16px',
                  background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                  borderRadius: '8px',
                  border: '1px solid #3b82f6',
                  marginBottom: '16px'
                }}>
                  <p style={{
                    margin: 0,
                    color: '#1e40af',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}>
                    Please provide the bid security reference number to search for the details to update in the public treasury.
                  </p>
                </div>
              )}

              {showForm && biddersStatus && (
                <div>
                  {/* Additional form rows */}
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: '600', color: '#374151' }}>Phone Number</span>}
                        name="phone_number"
                        rules={[
                          {
                            required: true,
                            message: 'Please enter Phone Number'
                          },
                          {
                            pattern: PHONE_PATTERN,
                            message: 'Phone number should be 10 digits.'
                          }
                        ]}
                      >
                        <Input
                          type="tel"
                          placeholder="Enter Phone Number"
                          disabled
                          size="large"
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: '600', color: '#374151' }}>National ID</span>}
                        name="bidder_national_id_number"
                        rules={[
                          {
                            required: true,
                            message: 'Please enter Your National ID'
                          },
                          { message: 'National ID should be 16 digits.' }
                        ]}
                      >
                        <Input
                          placeholder="National ID is missing"
                          maxLength={16}
                          disabled
                          size="large"
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* More form fields can be placed similarly */}
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: '600', color: '#374151' }}>Property ID</span>}
                        name="property_id"
                        rules={[{
                          required: true,
                          message: 'Please enter Property ID'
                        }]}
                      >
                        <Input
                          placeholder="Enter Property ID"
                          disabled
                          size="large"
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: '600', color: '#374151' }}>Execution Case Number</span>}
                        name="execution_case_number"
                        rules={[{
                          required: true,
                          message: 'Please enter Execution Case Number'
                        }]}
                      >
                        <Input
                          placeholder="Execution Case Number"
                          disabled
                          size="large"
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: '600', color: '#374151' }}>Transaction Reference</span>}
                        name="transaction_reference"
                        rules={[{
                          required: true,
                          message: 'Please enter transaction reference'
                        }]}
                      >
                        <Input
                          placeholder="Bid Reference Number"
                          disabled
                          size="large"
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: '600', color: '#374151' }}>Bidder&apos;s Names</span>}
                        name="fullname"
                        rules={[{
                          required: true,
                          message: 'Please enter Bidders Name'
                        }]}
                      >
                        <Input
                          placeholder="Bidders Name"
                          disabled
                          size="large"
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: '600', color: '#374151' }}>Deposit Bid Security Amount</span>}
                        name="amount"
                        rules={[{
                          required: true,
                          message: 'Please enter Amount'
                        }]}
                      >
                        <Input
                          type="text"
                          placeholder="Amount"
                          disabled
                          size="large"
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: '600', color: '#374151' }}>Currency</span>}
                        name="transaction_currency"
                        rules={[{
                          required: true,
                          message: 'Please provide the transaction currency'
                        }]}
                      >
                        <Input
                          maxLength={24}
                          placeholder="RWF"
                          disabled
                          size="large"
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: '600', color: '#374151' }}>Bid Security Payment Date</span>}
                        name="payment_date"
                        rules={[{
                          required: true,
                          message: 'Payment Date not Available'
                        }]}
                      >
                        <Input
                          placeholder="Bid Payment Date"
                          disabled
                          size="large"
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: '600', color: '#374151' }}>Email</span>}
                        name="email"
                        rules={[{
                          required: true,
                          message: 'Email not Available'
                        }]}
                      >
                        <Input
                          placeholder="Bidder Email"
                          disabled
                          size="large"
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: '600', color: '#374151' }}>Reason</span>}
                        name="reason"
                        rules={[
                          {
                            required: true,
                            message: 'Reason not available'
                          }
                        ]}
                      >
                        <Select
                          placeholder="Select a reason"
                          size="large"
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb'
                          }}
                        >
                          <Option value="Did not pay">Did not pay</Option>
                          <Option value="reason2">Others</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: '600', color: '#374151' }}>Assignee Bailiff</span>}
                        name="assignee-bailiff"
                        rules={[
                          {
                            required: true,
                            message: 'Assignee Bailiff not available'
                          }
                        ]}
                      >
                        <Input
                          placeholder="Assignee Bailiff"
                          size="large"
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb',
                            transition: 'border-color 0.2s ease'
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: '600', color: '#374151' }}>Bailiff Phone Number</span>}
                        name="bailiff_phone_number"
                        rules={[
                          {
                            required: true,
                            message: 'Phone Number is not Available'
                          },
                          {
                            pattern: /^\d{10}$/,
                            message: 'Phone Number must be exactly 10 digits'
                          },
                          {
                            len: 10,
                            message: 'Phone Number must be exactly 10 digits'
                          }
                        ]}
                      >
                        <Input
                          placeholder="Phone Number"
                          size="large"
                          maxLength={10}
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                              e.preventDefault()
                            }
                          }}
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb',
                            transition: 'border-color 0.2s ease'
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        label={<span style={{ fontWeight: '600', color: '#374151' }}>Bailiff National ID</span>}
                        name="bailiff_national_id"
                        rules={[
                          {
                            required: true,
                            message: 'Bailiff National ID is not Available'
                          },
                          {
                            pattern: /^\d{16}$/,
                            message: 'Bailiff National ID must be exactly 16 digits'
                          },
                          {
                            len: 16,
                            message: 'Bailiff National ID must be exactly 16 digits'
                          }
                        ]}
                      >
                        <Input
                          placeholder="Bailiff National ID"
                          size="large"
                          maxLength={16}
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                              e.preventDefault()
                            }
                          }}
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb',
                            transition: 'border-color 0.2s ease'
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                      <Form.Item
                        label={<span style={{ fontWeight: '600', color: '#374151' }}>Description</span>}
                        name="description"
                        rules={[
                          {
                            required: true,
                            message: 'Description is required'
                          }
                        ]}
                        labelAlign="left"
                        labelCol={{
                          span: 24
                        }}
                        wrapperCol={{
                          span: 24
                        }}
                      >
                        <TextArea
                          placeholder="Enter a description"
                          rows={4}
                          style={{
                            width: '100%',
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb',
                            transition: 'border-color 0.2s ease'
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} style={{ display: 'none' }}>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        name="bidder_id"
                      >
                        <Input
                          className="border-none disabled:bg-white disabled:text-white disabled:p-0 font-bold disabled:cursor-default placeholder:hidden"
                          disabled
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                      <Form.Item
                        name="bid_security_id"
                      >
                        <Input
                          type="text"
                          className="border-none disabled:bg-white disabled:text-white disabled:p-0 font-bold disabled:cursor-default"
                          disabled
                        />
                      </Form.Item>
                      {loadingRefund && <Spin size="large" />}
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={12} lg={8} xl={8}>
                      <Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          disabled={loadingRefund}
                          onClick={handleContinue}
                          size="large"
                          className="w-full font-bold"
                          style={{
                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                            border: 'none',
                            borderRadius: '8px',
                            height: '48px',
                            fontSize: '16px',
                            fontWeight: '600',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!loadingRefund) {
                              e.currentTarget.style.transform = 'translateY(-1px)'
                              e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)'
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!loadingRefund) {
                              e.currentTarget.style.transform = 'translateY(0)'
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)'
                            }
                          }}
                        >
                          {isEdit ? 'Update' : 'Save'}
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              )}
            </Card>
          </Form>
        </div>
      </div>

      <Row justify="center" align="middle" style={{ height: '100%' }}>
        <Col xs={24} md={24} xl={24}>
          {showSuccessAlert && (
            <Modal
              visible={showSuccessAlert}
              onCancel={() => {
                setShowSuccessAlert(false)
              }}
              footer={null}
              centered
            >
              <Alert
                message="Informational Notes"
                /* eslint-disable-next-line @typescript-eslint/restrict-template-expressions */
                description="Refund Request Submitted. Waiting to be Refunded..."
                type="info"
                showIcon
                style={{
                  width: '100%'
                }} // Set alert background color
                onClose={() => {
                  setShowSuccessAlert(false)
                }}
              />
            </Modal>
          )}
        </Col>
      </Row>
      <Row justify="center" align="middle" style={{ height: '100%' }}>
        <Col xs={24} md={24} xl={24}>
          {showWarningAlert && (
            <Modal
              visible={showWarningAlert}
              onCancel={() => {
                setShowWarningAlert(false)
              }}
              footer={null}
              centered
            >
              <Alert
                message="Informational Notes"
                /* eslint-disable-next-line @typescript-eslint/restrict-template-expressions */
                description={
                  'Refund Request already exists with the same Transaction Reference Number:'
                }
                type="info"
                showIcon
                style={{
                  width: '100%'
                }} // Set alert background color
                onClose={() => {
                  setShowWarningAlert(false)
                }}
              />
            </Modal>
          )}
        </Col>
      </Row>
      <Row justify="center" align="middle" style={{ height: '100%' }}>
        <Col xs={24} md={24} xl={24}>
          {showRequestExistsModal && (
            <Modal
              visible={showRequestExistsModal}
              onCancel={() => {
                setShowRequestExistsModal(false)
                setRequestExistsErrorMessage('')
              }}
              footer={null}
              centered
              width={500}
              style={{
                borderRadius: '12px'
              }}
            >
              <div style={{
                padding: '24px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  margin: '0 auto 20px',
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)'
                }}>
                  <span style={{ fontSize: '32px' }}>⚠️</span>
                </div>
                <h3 style={{
                  margin: '0 0 12px 0',
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#1f2937'
                }}>
                  Request Already Exists
                </h3>
                <p style={{
                  margin: '0 0 24px 0',
                  fontSize: '14px',
                  color: '#6b7280',
                  lineHeight: '1.6',
                  wordBreak: 'break-word'
                }}>
                  {requestExistsErrorMessage !== '' ? requestExistsErrorMessage : 'A refund request with this transaction reference number already exists in the system. Please verify the reference number or contact support if you believe this is an error.'}
                </p>
                <Button
                  type="primary"
                  onClick={() => {
                    setShowRequestExistsModal(false)
                    setRequestExistsErrorMessage('')
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    height: '40px',
                    padding: '0 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)'
                  }}
                >
                  Understood
                </Button>
              </div>
            </Modal>
          )}
        </Col>
      </Row>
      <Row justify="center" align="middle" style={{ height: '100%' }}>
        <Col xs={24} md={24} xl={24}>
          {showSuccessModal && (
            <Modal
              visible={showSuccessModal}
              onCancel={() => {
                setShowSuccessModal(false)
              }}
              footer={null}
              centered
              width={500}
              style={{
                borderRadius: '12px'
              }}
            >
              <div style={{
                padding: '24px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  margin: '0 auto 20px',
                  background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}>
                  <span style={{ fontSize: '32px' }}>✅</span>
                </div>
                <h3 style={{
                  margin: '0 0 12px 0',
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#1f2937'
                }}>
                  Success
                </h3>
                <p style={{
                  margin: '0 0 24px 0',
                  fontSize: '14px',
                  color: '#6b7280',
                  lineHeight: '1.6'
                }}>
                  {successMessage !== '' ? successMessage : '5% of Bid Security has been updated to public treasury'}
                </p>
                <Button
                  type="primary"
                  onClick={() => {
                    setShowSuccessModal(false)
                    setSuccessMessage('')
                    form.resetFields()
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    height: '40px',
                    padding: '0 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)'
                  }}
                >
                  Close
                </Button>
              </div>
            </Modal>
          )}
        </Col>
      </Row>
      {showCancelMessage && (
        <div className="cancel-message">
          <div className="separator"></div>
          <p>
            <h3>Thank you for using our services.</h3>
          </p>
          <div className="separator"></div>
          <p>
            For any other support, please contact us on
            <span>
              <strong> iecms-support@minijust.gov.rw</strong>
            </span>
            .
          </p>
        </div>
      )}
    </div>
  )
}

export default UpdateForPublicTreasury
